<template>
  <modal
    name="draw-dialog"
    class="draw-dialog-modal"
    :width="440"
    height="auto"
    :adaptive="true"
    :scrollable="true"
    @opened="focusInput"
    @closed="$emit('closed')"
  >
    <form
      class="draw-dialog"
      :class="`draw-dialog--${tone}`"
      role="dialog"
      aria-modal="true"
      aria-labelledby="draw-dialog-title"
      @submit.prevent="$emit('confirm')"
    >
      <header class="draw-dialog__header">
        <div class="draw-dialog__heading">
          <span class="draw-dialog__icon" aria-hidden="true">
            <font-awesome-icon :icon="dialogIcon" />
          </span>

          <h2 id="draw-dialog-title">
            {{ title }}
          </h2>
        </div>

        <button
          type="button"
          class="draw-dialog__close"
          title="Cerrar"
          aria-label="Cerrar diálogo"
          @click="$emit('cancel')"
        >
          <font-awesome-icon icon="times" />
        </button>
      </header>

      <div class="draw-dialog__body">
        <p v-if="message" class="draw-dialog__message">
          {{ message }}
        </p>

        <label v-if="inputVisible" class="draw-dialog__field">
          <span>{{ inputLabel }}</span>

          <input
            ref="dialogInput"
            :value="value"
            type="text"
            :placeholder="placeholder"
            maxlength="200"
            autocomplete="off"
            @input="$emit('input', $event.target.value)"
          >
        </label>

        <p
          v-if="error"
          class="draw-dialog__error"
          role="alert"
        >
          {{ error }}
        </p>
      </div>

      <footer class="draw-dialog__actions">
        <button
          v-if="showCancel"
          type="button"
          class="draw-dialog__button draw-dialog__button--secondary"
          @click="$emit('cancel')"
        >
          {{ cancelLabel }}
        </button>

        <button
          type="submit"
          class="draw-dialog__button"
          :class="confirmButtonClass"
        >
          {{ confirmLabel }}
        </button>
      </footer>
    </form>
  </modal>
</template>

<script>
export default {
  name: 'DrawDialog',
  props: {
    title: {
      type: String,
      required: true
    },
    message: {
      type: String,
      default: ''
    },
    tone: {
      type: String,
      default: 'info'
    },
    value: {
      type: String,
      default: ''
    },
    inputVisible: {
      type: Boolean,
      default: false
    },
    inputLabel: {
      type: String,
      default: 'Texto'
    },
    placeholder: {
      type: String,
      default: ''
    },
    error: {
      type: String,
      default: ''
    },
    showCancel: {
      type: Boolean,
      default: false
    },
    confirmLabel: {
      type: String,
      default: 'Aceptar'
    },
    cancelLabel: {
      type: String,
      default: 'Cancelar'
    }
  },
  computed: {
    dialogIcon() {
      if (this.inputVisible) return 'font'
      if (this.tone === 'danger' || this.tone === 'warning') {
        return 'exclamation-triangle'
      }
      return 'info-circle'
    },
    confirmButtonClass() {
      return this.tone === 'danger'
        ? 'draw-dialog__button--danger'
        : 'draw-dialog__button--primary'
    }
  },
  methods: {
    focusInput() {
      if (!this.inputVisible) return
      this.$nextTick(() => {
        if (this.$refs.dialogInput) {
          this.$refs.dialogInput.focus()
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.draw-dialog-modal::v-deep .vm--modal {
  overflow: visible;

  background: transparent;
  box-shadow: none;
}

.draw-dialog {
  overflow: hidden;

  width: 100%;

  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: 8px;
  box-shadow: 0 18px 48px rgba(16, 42, 67, 0.24);

  color: var(--color-graphite);
}

.draw-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  padding: 18px 20px;

  background: var(--color-navy);
  border-bottom: 1px solid var(--color-navy);
}

.draw-dialog__heading {
  display: flex;
  align-items: center;
  gap: 10px;
}

.draw-dialog__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 32px;
  height: 32px;

  background: rgba(255, 255, 255, 0.14);
  border-radius: 50%;

  color: var(--color-white);
}

.draw-dialog--warning .draw-dialog__icon {
  background: rgba(245, 130, 32, 0.24);
  color: #ffd19f;
}

.draw-dialog--danger .draw-dialog__icon {
  background: rgba(255, 255, 255, 0.14);
  color: #ffb8b8;
}

.draw-dialog__header h2 {
  margin: 0;

  color: var(--color-white);
  font-size: 1.05rem;
  font-weight: 700;
}

.draw-dialog__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 32px;
  height: 32px;
  padding: 0;

  background: rgba(255, 255, 255, 0.1);
  border: 1px solid transparent;
  border-radius: 50%;

  color: var(--color-white);
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 255, 255, 0.32);
    color: var(--color-white);
  }
}

.draw-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 14px;

  padding: 20px;

  background: var(--color-mist);
}

.draw-dialog__message {
  margin: 0;

  color: var(--color-graphite);
  font-size: 0.88rem;
  line-height: 1.5;
}

.draw-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 7px;

  margin: 0;

  color: var(--color-navy);
  font-size: 0.8rem;
  font-weight: 600;

  input {
    width: 100%;
    min-height: 40px;
    box-sizing: border-box;
    padding: 8px 10px;

    background: var(--color-white);
    border: 1px solid var(--color-line);
    border-radius: 4px;

    color: var(--color-graphite);
    font: inherit;

    &:focus {
      border-color: var(--color-steel);
      outline: 2px solid var(--color-fog);
      outline-offset: 1px;
    }
  }
}

.draw-dialog__error {
  margin: 0;

  color: var(--color-danger);
  font-size: 0.8rem;
  font-weight: 600;
}

.draw-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  padding: 14px 20px;

  background: var(--color-white);
  border-top: 1px solid var(--color-line);
}

.draw-dialog__button {
  min-width: 96px;
  min-height: 38px;
  padding: 8px 14px;

  border: 1px solid transparent;
  border-radius: 4px;

  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 700;

  cursor: pointer;
}

.draw-dialog__button--secondary {
  background: var(--color-white);
  border-color: var(--color-line);
  color: var(--color-navy);

  &:hover {
    background: var(--color-mist);
    border-color: var(--color-steel);
  }
}

.draw-dialog__button--primary {
  background: var(--color-orange);
  border-color: var(--color-orange);
  color: var(--color-white);

  &:hover {
    filter: brightness(0.94);
  }
}

.draw-dialog__button--danger {
  background: var(--color-danger);
  border-color: var(--color-danger);
  color: var(--color-white);

  &:hover {
    filter: brightness(0.92);
  }
}

@media (max-width: 520px) {
  .draw-dialog__actions {
    flex-direction: column-reverse;
  }

  .draw-dialog__button {
    width: 100%;
  }
}
</style>
